import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  BarChart3, TrendingUp, TrendingDown, Minus, Calendar, Users, Gavel, Briefcase,
  ArrowUp, ArrowDown, Loader2, RefreshCw, DollarSign, Activity, Clock, CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line
} from 'recharts';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS_ANIO = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value || 0);
};

const formatNumber = (value) => {
  return new Intl.NumberFormat('es-ES').format(value || 0);
};

const VariationBadge = ({ pct, invertir = false }) => {
  if (pct === null || pct === undefined || isNaN(pct)) {
    return <Badge variant="secondary" className="bg-gray-100 text-gray-600">—</Badge>;
  }
  if (pct === 0) {
    return (
      <Badge variant="secondary" className="bg-gray-100 text-gray-700 gap-1">
        <Minus className="w-3 h-3" /> 0%
      </Badge>
    );
  }
  const sube = pct > 0;
  const positivo = invertir ? !sube : sube;
  const cls = positivo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  const Icon = sube ? ArrowUp : ArrowDown;
  return (
    <Badge variant="secondary" className={`${cls} gap-1`} data-testid="variation-badge">
      <Icon className="w-3 h-3" /> {Math.abs(pct).toFixed(1)}%
    </Badge>
  );
};

const EstadisticasAnualTab = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [aniosDisponibles, setAniosDisponibles] = useState([]);
  const [aniosSeleccionados, setAniosSeleccionados] = useState([]);

  // Cargar años disponibles inicial
  useEffect(() => {
    const anioActual = new Date().getFullYear();
    const inicial = [anioActual - 2, anioActual - 1, anioActual];
    setAniosDisponibles(Array.from({ length: 10 }, (_, i) => anioActual - i));
    setAniosSeleccionados(inicial);
  }, []);

  useEffect(() => {
    if (aniosSeleccionados.length > 0) {
      loadStats();
    }
  }, [aniosSeleccionados]);

  const loadStats = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const anios = aniosSeleccionados.sort((a, b) => a - b).join(',');
      const res = await axios.get(`${API}/api/estadisticas/expedientes/anual?anios=${anios}`);
      setStats(res.data);
    } catch (error) {
      console.error('Error cargando estadísticas anuales:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar las estadísticas anuales', variant: 'destructive' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const toggleAnio = (anio) => {
    setAniosSeleccionados((prev) => {
      if (prev.includes(anio)) {
        if (prev.length === 1) {
          toast({ title: 'Aviso', description: 'Selecciona al menos un año', variant: 'destructive' });
          return prev;
        }
        return prev.filter((a) => a !== anio);
      }
      if (prev.length >= 6) {
        toast({ title: 'Aviso', description: 'Máximo 6 años en comparativa', variant: 'destructive' });
        return prev;
      }
      return [...prev, anio];
    });
  };

  const aniosOrdenados = useMemo(() => [...aniosSeleccionados].sort((a, b) => a - b), [aniosSeleccionados]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="estadisticas-anual-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!stats || !stats.anios || stats.anios.length === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-500">No hay datos disponibles para los años seleccionados</p>
      </div>
    );
  }

  const { por_anio, comparativa_mensual, por_rama, por_responsable, por_tipo_procedimiento, resumen_comparativo } = stats;

  return (
    <div className="space-y-6" data-testid="estadisticas-anual-tab">
      {/* Selector de años */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Selecciona los años a comparar
              </CardTitle>
              <CardDescription>Hasta 6 años en la misma vista</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => loadStats(true)} disabled={refreshing} data-testid="anual-refresh-btn">
              {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Actualizar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {aniosDisponibles.map((anio) => {
              const activo = aniosSeleccionados.includes(anio);
              return (
                <button
                  key={anio}
                  onClick={() => toggleAnio(anio)}
                  className={`px-4 py-2 rounded-lg border-2 transition-all font-medium text-sm ${
                    activo
                      ? 'bg-blue-600 border-blue-600 text-white shadow-md'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-blue-300'
                  }`}
                  data-testid={`anio-toggle-${anio}`}
                >
                  {anio}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Tarjetas KPI por año */}
      <div className={`grid gap-4 ${aniosOrdenados.length <= 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6'}`}>
        {aniosOrdenados.map((anio, idx) => {
          const datos = por_anio[String(anio)] || {};
          const comparativa = resumen_comparativo.find((c) => c.anio === anio)?.metricas || {};
          const color = COLORS_ANIO[idx % COLORS_ANIO.length];
          return (
            <Card key={anio} className="border-2" style={{ borderColor: color }} data-testid={`kpi-card-${anio}`}>
              <CardHeader className="pb-2" style={{ backgroundColor: `${color}10` }}>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span style={{ color }}>{anio}</span>
                  <Briefcase className="w-5 h-5" style={{ color }} />
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-3 space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Creados</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{formatNumber(datos.creados)}</span>
                    {comparativa.creados && <VariationBadge pct={comparativa.creados.variacion_pct} />}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Cerrados</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{formatNumber(datos.cerrados)}</span>
                    {comparativa.cerrados && <VariationBadge pct={comparativa.cerrados.variacion_pct} />}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Activos fin año</span>
                  <span className="font-bold text-blue-700">{formatNumber(datos.activos_fin_anio)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Valor total</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-green-700">{formatCurrency(datos.valor_total)}</span>
                    {comparativa.valor_total && <VariationBadge pct={comparativa.valor_total.variacion_pct} />}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Duración media</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{datos.duracion_media_dias} días</span>
                    {comparativa.duracion_media_dias && <VariationBadge pct={comparativa.duracion_media_dias.variacion_pct} invertir />}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Tasa resolución</span>
                  <span className="font-bold">{datos.tasa_resolucion}%</span>
                </div>
                <div className="flex items-center justify-between border-t pt-2">
                  <span className="text-gray-600">Actuaciones</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-purple-700">{formatNumber(datos.actuaciones_totales)}</span>
                    {comparativa.actuaciones_totales && <VariationBadge pct={comparativa.actuaciones_totales.variacion_pct} />}
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Actuaciones/exp.</span>
                  <span>{datos.actuaciones_por_expediente}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Gráfico de barras: Creados vs Cerrados por año */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="chart-creados-cerrados">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Creados vs Cerrados por Año
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={aniosOrdenados.map((a) => ({
                anio: String(a),
                Creados: por_anio[String(a)]?.creados || 0,
                Cerrados: por_anio[String(a)]?.cerrados || 0,
                'Activos fin año': por_anio[String(a)]?.activos_fin_anio || 0
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="anio" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="Creados" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Cerrados" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Activos fin año" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card data-testid="chart-valor-economico">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Valor Económico por Año
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={aniosOrdenados.map((a) => ({
                anio: String(a),
                'Valor total': por_anio[String(a)]?.valor_total || 0,
                'Valor promedio': por_anio[String(a)]?.valor_promedio || 0
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="anio" />
                <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Legend />
                <Bar dataKey="Valor total" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Valor promedio" fill="#06B6D4" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Heatmap mensual */}
      <Card data-testid="heatmap-mensual">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-600" />
            Heatmap Mensual de Expedientes Creados
          </CardTitle>
          <CardDescription>Detecta estacionalidad y picos por mes</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={comparativa_mensual}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" />
              <YAxis />
              <Tooltip />
              <Legend />
              {aniosOrdenados.map((anio, idx) => (
                <Line
                  key={anio}
                  type="monotone"
                  dataKey={String(anio)}
                  stroke={COLORS_ANIO[idx % COLORS_ANIO.length]}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>

          {/* Tabla heatmap */}
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border p-2 text-left font-medium text-gray-700">Mes</th>
                  {aniosOrdenados.map((a) => (
                    <th key={a} className="border p-2 text-center font-medium text-gray-700">{a}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparativa_mensual.map((fila) => {
                  const maxVal = Math.max(...aniosOrdenados.map((a) => fila[String(a)] || 0), 1);
                  return (
                    <tr key={fila.mes}>
                      <td className="border p-2 font-medium">{fila.mes}</td>
                      {aniosOrdenados.map((a) => {
                        const v = fila[String(a)] || 0;
                        const intensidad = v / maxVal;
                        const bg = v === 0
                          ? '#f9fafb'
                          : `rgba(59, 130, 246, ${0.15 + intensidad * 0.7})`;
                        const color = intensidad > 0.5 ? 'white' : '#1f2937';
                        return (
                          <td
                            key={a}
                            className="border p-2 text-center font-medium transition-colors"
                            style={{ backgroundColor: bg, color }}
                          >
                            {v}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Comparativa por Rama Jurídica */}
      <Card data-testid="comparativa-ramas">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gavel className="w-5 h-5 text-amber-600" />
            Comparativa por Rama Jurídica
          </CardTitle>
          <CardDescription>Top 10 ramas con más expedientes</CardDescription>
        </CardHeader>
        <CardContent>
          {por_rama.datos.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">Sin datos suficientes</p>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={por_rama.datos} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="rama" type="category" width={140} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                {aniosOrdenados.map((anio, idx) => (
                  <Bar key={anio} dataKey={String(anio)} fill={COLORS_ANIO[idx % COLORS_ANIO.length]} radius={[0, 4, 4, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Comparativa por Responsable */}
      <Card data-testid="comparativa-responsables">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" />
            Carga de Trabajo por Responsable
          </CardTitle>
          <CardDescription>Expedientes creados con cada responsable (top 10)</CardDescription>
        </CardHeader>
        <CardContent>
          {por_responsable.datos.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">Sin datos de responsables asignados</p>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={por_responsable.datos} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="responsable" type="category" width={140} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                {aniosOrdenados.map((anio, idx) => (
                  <Bar key={anio} dataKey={String(anio)} fill={COLORS_ANIO[idx % COLORS_ANIO.length]} radius={[0, 4, 4, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Comparativa por Tipo de Procedimiento */}
      <Card data-testid="comparativa-tipos">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-rose-600" />
            Comparativa por Tipo de Procedimiento
          </CardTitle>
        </CardHeader>
        <CardContent>
          {por_tipo_procedimiento.datos.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">Sin datos suficientes</p>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={por_tipo_procedimiento.datos} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="tipo" type="category" width={180} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend />
                {aniosOrdenados.map((anio, idx) => (
                  <Bar key={anio} dataKey={String(anio)} fill={COLORS_ANIO[idx % COLORS_ANIO.length]} radius={[0, 4, 4, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Resumen ejecutivo año vs anterior */}
      <Card data-testid="resumen-ejecutivo">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Resumen Ejecutivo — Variación vs Año Anterior
          </CardTitle>
          <CardDescription>Variaciones porcentuales año a año</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="p-3 text-left font-semibold">Año</th>
                  <th className="p-3 text-center font-semibold">Creados</th>
                  <th className="p-3 text-center font-semibold">Cerrados</th>
                  <th className="p-3 text-center font-semibold">Valor total</th>
                  <th className="p-3 text-center font-semibold">Actuaciones</th>
                  <th className="p-3 text-center font-semibold">Duración media</th>
                  <th className="p-3 text-center font-semibold">Tasa resolución</th>
                </tr>
              </thead>
              <tbody>
                {resumen_comparativo.map((row, idx) => (
                  <tr key={row.anio} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="p-3 font-bold">{row.anio}</td>
                    <td className="p-3 text-center">
                      {row.metricas.creados
                        ? <VariationBadge pct={row.metricas.creados.variacion_pct} />
                        : <span className="text-gray-400 text-xs">base</span>}
                    </td>
                    <td className="p-3 text-center">
                      {row.metricas.cerrados
                        ? <VariationBadge pct={row.metricas.cerrados.variacion_pct} />
                        : <span className="text-gray-400 text-xs">base</span>}
                    </td>
                    <td className="p-3 text-center">
                      {row.metricas.valor_total
                        ? <VariationBadge pct={row.metricas.valor_total.variacion_pct} />
                        : <span className="text-gray-400 text-xs">base</span>}
                    </td>
                    <td className="p-3 text-center">
                      {row.metricas.actuaciones_totales
                        ? <VariationBadge pct={row.metricas.actuaciones_totales.variacion_pct} />
                        : <span className="text-gray-400 text-xs">base</span>}
                    </td>
                    <td className="p-3 text-center">
                      {row.metricas.duracion_media_dias
                        ? <VariationBadge pct={row.metricas.duracion_media_dias.variacion_pct} invertir />
                        : <span className="text-gray-400 text-xs">base</span>}
                    </td>
                    <td className="p-3 text-center">
                      {row.metricas.tasa_resolucion
                        ? <VariationBadge pct={row.metricas.tasa_resolucion.variacion_pct} />
                        : <span className="text-gray-400 text-xs">base</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            <Clock className="w-3 h-3 inline mr-1" />
            Para "Duración media de cierre", una variación negativa es <span className="text-green-700 font-medium">positiva</span> (los expedientes se cierran más rápido).
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default EstadisticasAnualTab;
